/**
 * components/client-detail/DocumentPreviewModal.tsx
 *
 * Modal for previewing client documents (PDFs in practice, since all uploads
 * are converted to PDF automatically by the convert-documents Edge Function).
 * - PDFs: rendered in an <iframe> without sandbox — the sandbox attribute
 *   blocks the browser's built-in PDF viewer in Chrome, Safari, Firefox and Edge.
 * - Images: displayed inline (fallback for unconverted documents).
 * - Other types: fallback message with a download button.
 *
 * Fetches the file from Supabase Storage as a Blob, forces the explicit MIME
 * type on it (Supabase may return a generic content-type), then creates a
 * temporary object URL. Revokes the URL on close.
 *
 * Safari iOS cannot render PDFs inline in an iframe — an "Open in new tab"
 * button is shown instead. This is coherent since the app runs in a browser.
 *
 * Created Mar 2026.
 * Updated Mar 2026: Escape key closes the modal.
 * Updated Apr 2026: Added optional showDownload prop (default true).
 * Updated Jun 2026: Removed sandbox attribute (was blocking PDF viewer in all
 *   major browsers). Force explicit MIME type on blob. Added "Ouvrir" button in
 *   header. Added Safari iOS detection with open-in-new-tab fallback. Added
 *   iframe onLoad render-error detection with graceful fallback.
 */

import { useEffect, useRef, useState } from 'react';
import { X, Download, Loader2, AlertCircle, FileText, File, ExternalLink } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import Button from '../ui/Button';

interface DocumentPreviewModalProps {
  storagePath: string;
  filename: string;
  mimeType: string;
  onClose: () => void;
  showDownload?: boolean;
}

function detectIosSafari(): boolean {
  const ua = navigator.userAgent;
  return (
    /iPad|iPhone|iPod/.test(ua) &&
    /Safari/.test(ua) &&
    !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua)
  );
}

export default function DocumentPreviewModal({
  storagePath,
  filename,
  mimeType,
  onClose,
  showDownload = true,
}: DocumentPreviewModalProps) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [renderError, setRenderError] = useState(false);
  const urlRef = useRef<string | null>(null);
  const iosSafari = detectIosSafari();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setObjectUrl(null);
      setLoading(true);
      setFetchError(false);
      setRenderError(false);

      const { data, error } = await supabase.storage
        .from('request-documents')
        .download(storagePath);

      if (cancelled) return;

      if (error || !data) {
        setFetchError(true);
        setLoading(false);
        return;
      }

      // Recreate the blob with explicit MIME type: Supabase may return a
      // generic content-type, which prevents the browser PDF viewer from activating.
      const typedBlob = new Blob([data], { type: mimeType });
      const url = URL.createObjectURL(typedBlob);
      urlRef.current = url;
      setObjectUrl(url);
      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [storagePath, mimeType]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // After the iframe fires onLoad, inspect its contentDocument. If the PDF
  // viewer is active, access is blocked (cross-origin SecurityError) — that
  // means success. If the document is accessible but empty, the browser
  // could not render the PDF and we fall back to the open-in-new-tab UI.
  const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    try {
      const doc = e.currentTarget.contentDocument;
      if (doc !== null && (!doc.body || doc.body.childNodes.length === 0)) {
        setRenderError(true);
      }
    } catch {
      // SecurityError: PDF viewer is active (cross-origin) — rendering succeeded.
    }
  };

  const handleDownload = () => {
    if (!objectUrl) return;
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleOpenInNewTab = () => {
    if (objectUrl) window.open(objectUrl, '_blank', 'noopener');
  };

  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';
  const isPreviewable = isImage || isPdf;
  const showFallback = isPdf && (iosSafari || renderError);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Aperçu de ${filename}`}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 flex flex-col bg-white rounded-2xl shadow-soft-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            {isImage ? (
              <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-primary-600" />
              </div>
            ) : isPdf ? (
              <div className="w-8 h-8 rounded-lg bg-error-50 flex items-center justify-center flex-shrink-0">
                <FileText className="w-4 h-4 text-error-600" />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                <File className="w-4 h-4 text-neutral-500" />
              </div>
            )}
            <p className="text-sm font-medium text-neutral-900 truncate">{filename}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            {objectUrl && (
              <Button type="button" variant="secondary" onClick={handleOpenInNewTab}>
                <ExternalLink className="w-4 h-4 mr-1.5" />
                Ouvrir
              </Button>
            )}
            {objectUrl && showDownload && (
              <Button type="button" variant="secondary" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-1.5" />
                Télécharger
              </Button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
              aria-label="Fermer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto min-h-0 bg-neutral-50">
          {loading && (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
              <p className="text-sm text-neutral-500">Chargement du document...</p>
            </div>
          )}

          {!loading && fetchError && (
            <div className="flex flex-col items-center justify-center h-64 gap-3 text-center px-6">
              <div className="w-12 h-12 rounded-full bg-error-50 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-error-500" />
              </div>
              <p className="text-sm font-medium text-neutral-700">Impossible de charger le document</p>
              <p className="text-sm text-neutral-400">Une erreur est survenue lors du chargement.</p>
            </div>
          )}

          {!loading && !fetchError && objectUrl && isImage && (
            <div className="flex items-center justify-center p-6 min-h-[300px]">
              <img
                src={objectUrl}
                alt={filename}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-soft"
              />
            </div>
          )}

          {/* PDF inline viewer — no sandbox to allow the browser's built-in PDF viewer */}
          {!loading && !fetchError && objectUrl && isPdf && !showFallback && (
            <iframe
              src={objectUrl}
              title={filename}
              className="w-full h-[70vh] border-0"
              onLoad={handleIframeLoad}
            />
          )}

          {/* Fallback for Safari iOS or render failures */}
          {!loading && !fetchError && objectUrl && showFallback && (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-6">
              <div className="w-12 h-12 rounded-full bg-primary-50 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-1">
                  L'aperçu n'est pas disponible dans ce navigateur
                </p>
                <p className="text-sm text-neutral-400">
                  Ouvrez le document dans un nouvel onglet pour le visualiser.
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-center">
                <Button type="button" variant="primary" onClick={handleOpenInNewTab}>
                  <ExternalLink className="w-4 h-4 mr-1.5" />
                  Ouvrir dans un nouvel onglet
                </Button>
                {showDownload && (
                  <Button type="button" variant="secondary" onClick={handleDownload}>
                    <Download className="w-4 h-4 mr-1.5" />
                    Télécharger
                  </Button>
                )}
              </div>
            </div>
          )}

          {!loading && !fetchError && objectUrl && !isPreviewable && (
            <div className="flex flex-col items-center justify-center h-64 gap-4 text-center px-6">
              <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
                <File className="w-6 h-6 text-neutral-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-neutral-700 mb-1">
                  Aperçu non disponible pour ce type de fichier
                </p>
                {showDownload ? (
                  <p className="text-sm text-neutral-400">
                    Utilisez le bouton Télécharger pour ouvrir ce document.
                  </p>
                ) : (
                  <p className="text-sm text-neutral-400">
                    Ce document sera inclus dans le dossier final.
                  </p>
                )}
              </div>
              {showDownload && (
                <Button type="button" variant="primary" onClick={handleDownload}>
                  <Download className="w-4 h-4 mr-1.5" />
                  Télécharger
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
